import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { CsvImportModal } from '@/components/CsvImportModal';
import { FilmGrid, type Film } from '@/components/FilmGrid';
import { FilmSearchModal } from '@/components/FilmSearchModal';
import {
  WatchlistModeToggle,
  ModeChangeWarningModal,
  type WatchlistMode,
} from '@/components/WatchlistModeToggle';
import { formatNextRentalTime } from '@/lib/timezone';
import { getApiBaseUrl } from '@/lib/api';

interface User {
  id: string;
  email: string;
  timezone: string;
  createdAt: string;
}

interface Watchlist {
  letterboxdUrl: string | null;
  mode: WatchlistMode;
  filmsCount: number;
  filmsUpdatedAt: string | null;
  createdAt: string;
  lastSyncAt: string | null;
  lastSyncMethod: 'server' | 'csv' | 'bookmarklet' | null;
}

interface Recipient {
  id: string;
  emailAddress: string;
  isOwner: boolean;
  isVerified: boolean;
  createdAt: string;
}

interface CurrentRental {
  id: string;
  movieTitle: string;
  movieYear: string;
  token: string;
  createdAt: string;
  emailSentAt: string | null;
  openedAt: string | null;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [currentRental, setCurrentRental] = useState<CurrentRental | null>(
    null
  );
  const [letterboxdUsername, setLetterboxdUsername] = useState('');
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [isSavingWatchlist, setIsSavingWatchlist] = useState(false);
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingRecipientId, setDeletingRecipientId] = useState<string | null>(
    null
  );
  const [idToken, setIdToken] = useState<string | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showFilmSearch, setShowFilmSearch] = useState(false);
  const [showModeChangeWarning, setShowModeChangeWarning] = useState(false);
  const [pendingMode, setPendingMode] = useState<WatchlistMode | null>(null);
  const [isChangingMode, setIsChangingMode] = useState(false);
  const [removingFilmId, setRemovingFilmId] = useState<string | null>(null);

  // Films state
  const [films, setFilms] = useState<Film[]>([]);
  const [loadingFilms, setLoadingFilms] = useState(false);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
      } catch {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch watchlist
  const fetchWatchlist = useCallback(async () => {
    if (!idToken) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch watchlist');
      }

      const data = await response.json();
      setWatchlist(data.watchlist);

      // Extract username from URL for display
      if (data.watchlist?.letterboxdUrl) {
        const match = data.watchlist.letterboxdUrl.match(
          /letterboxd\.com\/([\w.-]+)\//
        );
        if (match) {
          setLetterboxdUsername(match[1]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load watchlist');
    }
  }, [idToken]);

  // Fetch films
  const fetchFilms = useCallback(async () => {
    if (!idToken || !watchlist) return;

    setLoadingFilms(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist/films`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch films');
      }

      const data = await response.json();
      setFilms(data.films);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load films');
    } finally {
      setLoadingFilms(false);
    }
  }, [idToken, watchlist]);

  // Fetch recipients
  const fetchRecipients = useCallback(async () => {
    if (!idToken) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/recipients`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recipients');
      }

      const data = await response.json();
      setRecipients(data.recipients);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load recipients'
      );
    }
  }, [idToken]);

  // Fetch user profile
  const fetchUser = useCallback(async () => {
    if (!idToken) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/user`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await response.json();
      setUser(data.user);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load user profile'
      );
    }
  }, [idToken]);

  // Fetch current rental
  const fetchCurrentRental = useCallback(async () => {
    if (!idToken) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/rentals/current`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current rental');
      }

      const data = await response.json();
      setCurrentRental(data.rental);
    } catch (err) {
      // Don't show error for rentals - it's okay if there's none
      console.error('Failed to fetch current rental:', err);
    }
  }, [idToken]);

  useEffect(() => {
    if (idToken) {
      Promise.all([
        fetchUser(),
        fetchWatchlist(),
        fetchRecipients(),
        fetchCurrentRental(),
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [idToken, fetchUser, fetchWatchlist, fetchRecipients, fetchCurrentRental]);

  // Fetch films when watchlist is loaded
  useEffect(() => {
    if (watchlist) {
      fetchFilms();
    }
  }, [watchlist, fetchFilms]);

  const handleSaveWatchlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!letterboxdUsername.trim() || !idToken) return;

    setIsSavingWatchlist(true);
    setError(null);

    try {
      const letterboxdUrl = `https://letterboxd.com/${letterboxdUsername.trim()}/watchlist/`;

      const response = await fetch(`${getApiBaseUrl()}/watchlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ letterboxdUrl }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save watchlist');
      }

      setWatchlist(data.watchlist);
      setSuccessMessage('Letterboxd URL saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save watchlist');
    } finally {
      setIsSavingWatchlist(false);
    }
  };

  const handleImportCsv = async (
    csvContent: string
  ): Promise<{
    filmsImported: number;
    filmsProcessed?: number;
    filmsAdded?: number;
    skippedRows: number;
    skippedDuplicates?: number;
    filmsDeleted?: number;
  }> => {
    if (!idToken) throw new Error('Not authenticated');

    const response = await fetch(`${getApiBaseUrl()}/watchlist/import-csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ csvContent }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to import CSV');
    }

    // Refresh watchlist and films
    await fetchWatchlist();
    await fetchFilms();

    return {
      filmsImported: data.filmsImported,
      filmsProcessed: data.filmsProcessed,
      filmsAdded: data.filmsAdded,
      skippedRows: data.skippedRows,
      skippedDuplicates: data.skippedDuplicates,
      filmsDeleted: data.filmsDeleted,
    };
  };

  // Create a manual watchlist (for users without Letterboxd)
  const handleCreateManualWatchlist = async () => {
    if (!idToken) return;

    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ mode: 'manual' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create watchlist');
      }

      setWatchlist(data.watchlist);
      setSuccessMessage('Manual watchlist created! Start adding films.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create watchlist'
      );
    }
  };

  // Handle mode change request (show warning first)
  const handleModeChangeRequest = (newMode: WatchlistMode) => {
    setPendingMode(newMode);
    setShowModeChangeWarning(true);
  };

  // Confirm mode change
  const handleConfirmModeChange = async () => {
    if (!idToken || !pendingMode) return;

    setIsChangingMode(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ mode: pendingMode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change mode');
      }

      setWatchlist(data.watchlist);
      setShowModeChangeWarning(false);
      setPendingMode(null);
      setSuccessMessage(
        `Switched to ${pendingMode === 'manual' ? 'Manual' : 'Sync'} mode`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change mode');
    } finally {
      setIsChangingMode(false);
    }
  };

  // Add film to watchlist (manual mode)
  const handleAddFilm = async (film: {
    tmdbId: number;
    title: string;
    year: string;
  }) => {
    if (!idToken) throw new Error('Not authenticated');

    const response = await fetch(`${getApiBaseUrl()}/watchlist/films`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(film),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to add film');
    }

    // Add the new film to the local state
    setFilms((prev) => [...prev, data.film]);

    // Update watchlist count
    if (watchlist) {
      setWatchlist({
        ...watchlist,
        filmsCount: watchlist.filmsCount + 1,
        filmsUpdatedAt: new Date().toISOString(),
      });
    }
  };

  // Remove film from watchlist (manual mode)
  const handleRemoveFilm = async (filmId: string) => {
    if (!idToken) return;

    setRemovingFilmId(filmId);
    setError(null);

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/watchlist/films/${filmId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove film');
      }

      // Remove film from local state
      setFilms((prev) => prev.filter((f) => f.id !== filmId));

      // Update watchlist count
      if (watchlist) {
        setWatchlist({
          ...watchlist,
          filmsCount: Math.max(0, watchlist.filmsCount - 1),
          filmsUpdatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove film');
    } finally {
      setRemovingFilmId(null);
    }
  };

  const handleAddRecipient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRecipientEmail.trim() || !idToken) return;

    setIsAddingRecipient(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/recipients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ emailAddress: newRecipientEmail.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add recipient');
      }

      setRecipients([...recipients, data.recipient]);
      setNewRecipientEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add recipient');
    } finally {
      setIsAddingRecipient(false);
    }
  };

  const handleDeleteRecipient = async (id: string) => {
    if (!idToken) return;

    setDeletingRecipientId(id);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/recipients/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete recipient');
      }

      setRecipients(recipients.filter((r) => r.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete recipient'
      );
    } finally {
      setDeletingRecipientId(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch {
      setError('Failed to sign out');
    }
  };

  const handleTimezoneChange = async (newTimezone: string) => {
    if (!idToken || !user || newTimezone === user.timezone) return;

    setIsUpdatingTimezone(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/user`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ timezone: newTimezone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update timezone');
      }

      setUser(data.user);
      setSuccessMessage('Timezone updated successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update timezone'
      );
    } finally {
      setIsUpdatingTimezone(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!idToken) return;

    setIsDeletingAccount(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/user`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      // Sign out and redirect to home
      await signOut(auth);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      setIsDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-dark-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <nav className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="text-xl sm:text-2xl font-display font-bold text-primary"
            >
              Still Love Film
            </Link>
            <button
              onClick={handleSignOut}
              className="text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body text-sm sm:text-base min-h-[44px] px-2 touch-manipulation"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-dark mb-6 sm:mb-8">
            Settings
          </h1>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-500 text-sm underline mt-1"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Success Display */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Account Info Section */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Account
            </h2>

            {user && (
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-dark-500 mb-1">
                    Email
                  </label>
                  <p className="text-dark font-body">{user.email}</p>
                </div>

                {/* Timezone */}
                <div>
                  <label
                    htmlFor="timezone"
                    className="block text-sm font-medium text-dark-500 mb-1"
                  >
                    Timezone
                  </label>
                  <p className="text-sm text-dark-400 mb-2">
                    Your weekly film pick arrives every Friday at 9:00 AM in
                    your timezone.
                  </p>
                  <TimezoneSelector
                    value={user.timezone}
                    onChange={handleTimezoneChange}
                    disabled={isUpdatingTimezone}
                    isLoading={isUpdatingTimezone}
                  />
                  {/* Next rental arrival time */}
                  <div className="mt-3 p-3 bg-primary-50 rounded-lg">
                    <p className="text-sm text-primary-700">
                      <span className="font-medium">Next rental:</span>{' '}
                      {formatNextRentalTime(user.timezone)}
                    </p>
                  </div>
                </div>

                {/* Member since */}
                <div>
                  <label className="block text-sm font-medium text-dark-500 mb-1">
                    Member since
                  </label>
                  <p className="text-dark font-body">
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Current Rental Section */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Current Rental
            </h2>
            {currentRental ? (
              <div className="bg-dark-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-dark">
                      {currentRental.movieTitle} ({currentRental.movieYear})
                    </h3>
                    <p className="text-sm text-dark-500 mt-1">
                      {currentRental.openedAt
                        ? `Revealed on ${new Date(currentRental.openedAt).toLocaleDateString()}`
                        : currentRental.emailSentAt
                          ? 'Waiting to be revealed'
                          : 'Email not yet sent'}
                    </p>
                  </div>
                  <Link
                    to={`/reveal/${currentRental.token}`}
                    className="bg-primary text-white px-4 py-2 rounded-lg font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-colors text-sm min-h-[44px] flex items-center touch-manipulation"
                  >
                    {currentRental.openedAt ? 'View Again' : 'Reveal'}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-dark-400">
                <svg
                  className="h-12 w-12 mx-auto mb-3 text-dark-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
                <p className="font-body">No active rental</p>
                <p className="text-sm mt-1">
                  Your next film pick will arrive on Friday!
                </p>
              </div>
            )}
          </section>

          {/* Watchlist Section */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Your Watchlist
            </h2>

            {/* Mode Toggle - Always visible when watchlist exists */}
            {watchlist && (
              <div className="mb-4 sm:mb-6">
                <WatchlistModeToggle
                  mode={watchlist.mode}
                  onModeChange={handleModeChangeRequest}
                  hasLetterboxdUrl={!!watchlist.letterboxdUrl}
                  filmsCount={watchlist.filmsCount}
                  disabled={isChangingMode}
                />
              </div>
            )}

            {/* Description based on mode */}
            <p className="text-dark-500 font-body mb-4 sm:mb-6 text-sm sm:text-base">
              {watchlist?.mode === 'manual'
                ? 'Add films manually to your watchlist to receive personalized recommendations.'
                : 'Connect your Letterboxd watchlist to receive personalized film recommendations.'}
            </p>

            {/* Letterboxd Username Form - Only show when no watchlist exists */}
            {!watchlist && (
              <form onSubmit={handleSaveWatchlist} className="mb-4 sm:mb-6">
                <label
                  htmlFor="letterboxd-username"
                  className="block text-sm font-medium text-dark mb-2"
                >
                  Your Letterboxd username
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex items-stretch rounded-lg border border-dark-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary-100 transition-colors overflow-hidden">
                    <span className="inline-flex items-center px-3 bg-dark-50 text-dark-500 text-sm border-r border-dark-200 whitespace-nowrap">
                      letterboxd.com/
                    </span>
                    <input
                      type="text"
                      id="letterboxd-username"
                      value={letterboxdUsername}
                      onChange={(e) => setLetterboxdUsername(e.target.value)}
                      placeholder="username"
                      disabled={isSavingWatchlist}
                      className="flex-1 px-3 py-3 outline-none disabled:opacity-50 disabled:bg-dark-50 text-base min-w-0"
                    />
                    <span className="inline-flex items-center px-3 bg-dark-50 text-dark-500 text-sm border-l border-dark-200 whitespace-nowrap">
                      /watchlist/
                    </span>
                  </div>
                  <button
                    type="submit"
                    disabled={isSavingWatchlist || !letterboxdUsername.trim()}
                    className="bg-primary text-white px-6 py-3 rounded-lg font-medium active:bg-primary-800 sm:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap min-h-[44px] touch-manipulation"
                  >
                    {isSavingWatchlist ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            )}

            {/* Watchlist Status and Films */}
            {watchlist && (
              <div className="border-t border-dark-200 pt-4">
                {/* Sync status info - only in sync mode */}
                {watchlist.mode === 'sync' && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-dark-500 mb-4">
                    <span>
                      {watchlist.filmsCount} film
                      {watchlist.filmsCount !== 1 ? 's' : ''}
                    </span>
                    {watchlist.lastSyncAt && watchlist.lastSyncMethod && (
                      <span>
                        Last sync:{' '}
                        {new Date(watchlist.lastSyncAt).toLocaleDateString(
                          undefined,
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}{' '}
                        (
                        {watchlist.lastSyncMethod === 'csv'
                          ? 'CSV import'
                          : watchlist.lastSyncMethod === 'bookmarklet'
                            ? 'Bookmarklet'
                            : 'Auto sync'}
                        )
                      </span>
                    )}
                  </div>
                )}

                {/* Controls based on mode */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {watchlist.mode === 'manual' ? (
                    /* Manual Mode Controls */
                    <>
                      <button
                        onClick={() => setShowFilmSearch(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Add Film
                      </button>
                      <button
                        onClick={() => setShowCsvImport(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        Import CSV
                      </button>
                    </>
                  ) : (
                    /* Sync Mode Controls - CSV import and bookmarklet */
                    <>
                      <button
                        onClick={() => setShowCsvImport(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        Import CSV
                      </button>
                      <Link
                        to="/bookmarklet"
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                          />
                        </svg>
                        Get Bookmarklet
                      </Link>
                    </>
                  )}
                </div>

                {/* Film count in manual mode */}
                {watchlist.mode === 'manual' && (
                  <div className="mb-4 text-sm text-dark-500">
                    {watchlist.filmsCount} film
                    {watchlist.filmsCount !== 1 ? 's' : ''} in your watchlist
                  </div>
                )}

                {/* Film Grid */}
                <FilmGrid
                  films={films}
                  isLoading={loadingFilms || removingFilmId !== null}
                  isManualMode={watchlist.mode === 'manual'}
                  onRemoveFilm={
                    watchlist.mode === 'manual' ? handleRemoveFilm : undefined
                  }
                  emptyMessage={
                    watchlist.mode === 'manual'
                      ? 'No films yet. Click "Add Film" to start building your watchlist.'
                      : 'No films synced yet. Import a CSV or use the bookmarklet to add films from your Letterboxd watchlist.'
                  }
                />
              </div>
            )}

            {!watchlist && (
              <div className="text-center py-8 text-dark-400 border-t border-dark-200 mt-4">
                <p className="font-body mb-4">
                  Enter your Letterboxd username above to sync your watchlist
                  automatically.
                </p>
                <p className="text-sm text-dark-400 mb-4">Or</p>
                <button
                  onClick={handleCreateManualWatchlist}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-white border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Start with Manual Mode
                </button>
              </div>
            )}
          </section>

          {/* Recipients Section */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Email Recipients
            </h2>
            <p className="text-dark-500 font-body mb-4 sm:mb-6 text-sm sm:text-base">
              Add email addresses to receive weekly film recommendations. Each
              recipient needs to verify their email.
            </p>

            {/* Add Recipient Form */}
            <form onSubmit={handleAddRecipient} className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={newRecipientEmail}
                  onChange={(e) => setNewRecipientEmail(e.target.value)}
                  placeholder="friend@example.com"
                  disabled={isAddingRecipient}
                  className="flex-1 px-4 py-3 rounded-lg border border-dark-200 focus:border-primary focus:ring-2 focus:ring-primary-100 outline-none transition-colors disabled:opacity-50 disabled:bg-dark-50 text-base"
                />
                <button
                  type="submit"
                  disabled={isAddingRecipient || !newRecipientEmail.trim()}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-medium active:bg-primary-800 sm:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap min-h-[44px] touch-manipulation"
                >
                  {isAddingRecipient ? 'Adding...' : 'Add Recipient'}
                </button>
              </div>
              <p className="text-sm text-dark-400 mt-2">
                A verification email will be sent to new recipients
              </p>
            </form>

            {/* Recipients List */}
            {recipients.length === 0 ? (
              <div className="text-center py-8 text-dark-400">
                <p className="font-body">No recipients added yet.</p>
                <p className="text-sm mt-1">
                  Add yourself or others to receive film recommendations!
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {recipients.map((recipient) => (
                  <li
                    key={recipient.id}
                    className="flex items-center justify-between p-4 bg-dark-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-dark truncate">
                          {recipient.emailAddress}
                        </span>
                        {recipient.isOwner && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                            You
                          </span>
                        )}
                        {recipient.isVerified ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                    {!recipient.isOwner && (
                      <button
                        onClick={() => handleDeleteRecipient(recipient.id)}
                        disabled={deletingRecipientId === recipient.id}
                        className="ml-2 sm:ml-4 p-2 text-dark-400 active:text-red-500 sm:hover:text-red-500 disabled:opacity-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                        title="Remove recipient"
                      >
                        {deletingRecipientId === recipient.id ? (
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Danger Zone */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-red-200">
            <h2 className="text-lg sm:text-xl font-display font-bold text-red-600 mb-4">
              Danger Zone
            </h2>
            <p className="text-dark-500 font-body mb-4 text-sm sm:text-base">
              Once you delete your account, there is no going back. All your
              data including your watchlist, recipients, and rental history will
              be permanently deleted.
            </p>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-white text-red-600 border-2 border-red-600 px-6 py-3 rounded-lg font-medium active:bg-red-50 sm:hover:bg-red-50 transition-colors min-h-[44px] touch-manipulation"
            >
              Delete Account
            </button>
          </section>

          {/* Back Link */}
          <Link
            to="/dashboard"
            className="inline-flex items-center text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body min-h-[44px] touch-manipulation"
          >
            <svg
              className="h-5 w-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>
        </div>
      </main>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Delete Account?
            </h3>
            <p className="text-dark-500 font-body mb-6 text-sm sm:text-base">
              Are you sure you want to delete your account? This action cannot
              be undone. All your data including your watchlist, recipients, and
              rental history will be permanently deleted.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingAccount}
                className="px-6 py-3 rounded-lg font-medium text-dark-500 active:bg-dark-50 sm:hover:bg-dark-50 transition-colors disabled:opacity-50 min-h-[44px] touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium active:bg-red-700 sm:hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              >
                {isDeletingAccount ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvImport && (
        <CsvImportModal
          isOpen={true}
          onClose={() => setShowCsvImport(false)}
          onImport={handleImportCsv}
          mode={watchlist?.mode || 'sync'}
          currentFilmsCount={watchlist?.filmsCount || 0}
        />
      )}

      {/* Film Search Modal (Manual Mode) */}
      {showFilmSearch && idToken && (
        <FilmSearchModal
          isOpen={true}
          onClose={() => setShowFilmSearch(false)}
          onAddFilm={handleAddFilm}
          idToken={idToken}
        />
      )}

      {/* Mode Change Warning Modal */}
      {showModeChangeWarning && pendingMode && (
        <ModeChangeWarningModal
          isOpen={true}
          onClose={() => {
            setShowModeChangeWarning(false);
            setPendingMode(null);
          }}
          onConfirm={handleConfirmModeChange}
          targetMode={pendingMode}
          filmsCount={watchlist?.filmsCount || 0}
          isLoading={isChangingMode}
        />
      )}
    </div>
  );
}
